import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useCreateInmopublicMembershipMutation } from '../inmopublicMembershipsApi';
import { Plus } from 'lucide-react';
import {
  createMembershipSchema,
  type CreateMembershipFormData,
  type CreateMembershipResponse,
} from './CreateMembershipDialog.types';

export const CreateMembershipDialog = () => {
  const [open, setOpen] = useState(false);
  const [createdMembership, setCreatedMembership] = useState<CreateMembershipResponse | null>(null);

  const [createMembership, { isLoading }] = useCreateInmopublicMembershipMutation();

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setError,
  } = useForm<CreateMembershipFormData>({
    resolver: zodResolver(createMembershipSchema),
    defaultValues: {
      accountId: '',
      userId: '',
      role: 'member',
      status: 'active',
      invitedBy: '',
    },
  });

  const onSubmit = async (data: CreateMembershipFormData) => {
    try {
      const submitData = {
        accountId: data.accountId.trim(),
        userId: data.userId.trim(),
        role: data.role,
        status: data.status,
        invitedBy: data.invitedBy?.trim() || undefined,
      };

      const result = await createMembership(submitData).unwrap();
      setCreatedMembership(result);
    } catch (error: any) {
      console.error('Error creating membership:', error);
      if (error?.data?.message?.includes('already exists')) {
        setError('accountId', { message: 'Membership already exists for this user and account' });
      } else if (error?.data?.message?.includes('User not found')) {
        setError('userId', { message: 'User not found' });
      } else if (error?.data?.message?.includes('Account not found')) {
        setError('accountId', { message: 'Account not found' });
      } else if (error?.data?.message?.includes('Inviter user not found')) {
        setError('invitedBy', { message: 'Inviter user not found' });
      } else {
        setError('accountId', { message: 'Error creating membership. Please try again.' });
      }
    }
  };

  const handleClose = () => {
    setOpen(false);
    reset();
    setCreatedMembership(null);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className='bg-blue-600 hover:bg-blue-700 text-white'>
          <Plus className='h-4 w-4 mr-2' />
          Create Membership
        </Button>
      </DialogTrigger>
      <DialogContent className='sm:max-w-[500px] bg-slate-800 border-slate-700'>
        <DialogHeader>
          <DialogTitle className='text-white'>
            {createdMembership ? 'Membership Created Successfully' : 'Create New Membership'}
          </DialogTitle>
        </DialogHeader>

        {createdMembership ? (
          <div className='space-y-6'>
            <div className='bg-green-900/20 border border-green-700 rounded-lg p-4'>
              <h3 className='text-green-400 font-medium mb-2'>Membership Created Successfully!</h3>
              <div className='space-y-2 text-sm'>
                <p>
                  <span className='text-slate-400'>Membership ID:</span>{' '}
                  <span className='text-white font-mono text-xs'>{createdMembership.membership.id}</span>
                </p>
                <p>
                  <span className='text-slate-400'>Account ID:</span>{' '}
                  <span className='text-white font-mono text-xs'>{createdMembership.membership.accountId}</span>
                </p>
                <p>
                  <span className='text-slate-400'>User ID:</span>{' '}
                  <span className='text-white font-mono text-xs'>{createdMembership.membership.userId}</span>
                </p>
                <p>
                  <span className='text-slate-400'>Role:</span>{' '}
                  <span className='text-white'>{createdMembership.membership.role}</span>
                </p>
                <p>
                  <span className='text-slate-400'>Status:</span>{' '}
                  <span className='text-white'>{createdMembership.membership.status}</span>
                </p>
                {createdMembership.membership.joinedAt && (
                  <p>
                    <span className='text-slate-400'>Joined At:</span>{' '}
                    <span className='text-white'>{new Date(createdMembership.membership.joinedAt).toLocaleString()}</span>
                  </p>
                )}
                {createdMembership.membership.invitedBy && (
                  <p>
                    <span className='text-slate-400'>Invited By:</span>{' '}
                    <span className='text-white font-mono text-xs'>{createdMembership.membership.invitedBy}</span>
                  </p>
                )}
              </div>
            </div>

            <div className='flex justify-end'>
              <Button onClick={handleClose} className='bg-blue-600 hover:bg-blue-700 text-white'>
                Close
              </Button>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit(onSubmit)} className='space-y-4'>
            <div className='space-y-2'>
              <Label htmlFor='accountId' className='text-slate-300'>
                Account ID *
              </Label>
              <Input
                id='accountId'
                {...register('accountId')}
                className='bg-slate-700 border-slate-600 text-white placeholder:text-slate-400'
                placeholder='507f1f77bcf86cd799439011'
              />
              {errors.accountId && <p className='text-red-400 text-sm'>{errors.accountId.message}</p>}
            </div>

            <div className='space-y-2'>
              <Label htmlFor='userId' className='text-slate-300'>
                User ID *
              </Label>
              <Input
                id='userId'
                {...register('userId')}
                className='bg-slate-700 border-slate-600 text-white placeholder:text-slate-400'
                placeholder='507f1f77bcf86cd799439012'
              />
              {errors.userId && <p className='text-red-400 text-sm'>{errors.userId.message}</p>}
            </div>

            <div className='grid grid-cols-2 gap-4'>
              <div className='space-y-2'>
                <Label htmlFor='role' className='text-slate-300'>
                  Role
                </Label>
                <select
                  id='role'
                  {...register('role')}
                  className='w-full px-3 py-2 bg-slate-700 border border-slate-600 text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500'
                >
                  <option value='member'>Member</option>
                  <option value='admin'>Admin</option>
                  <option value='viewer'>Viewer</option>
                </select>
                {errors.role && <p className='text-red-400 text-sm'>{errors.role.message}</p>}
              </div>

              <div className='space-y-2'>
                <Label htmlFor='status' className='text-slate-300'>
                  Status
                </Label>
                <select
                  id='status'
                  {...register('status')}
                  className='w-full px-3 py-2 bg-slate-700 border border-slate-600 text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500'
                >
                  <option value='active'>Active</option>
                  <option value='inactive'>Inactive</option>
                  <option value='invited'>Invited</option>
                </select>
                {errors.status && <p className='text-red-400 text-sm'>{errors.status.message}</p>}
              </div>
            </div>

            <div className='space-y-2'>
              <Label htmlFor='invitedBy' className='text-slate-300'>
                Invited By (User ID)
              </Label>
              <Input
                id='invitedBy'
                {...register('invitedBy')}
                className='bg-slate-700 border-slate-600 text-white placeholder:text-slate-400'
                placeholder='507f1f77bcf86cd799439013'
              />
              {errors.invitedBy && <p className='text-red-400 text-sm'>{errors.invitedBy.message}</p>}
            </div>

            <div className='flex justify-end space-x-2 pt-4'>
              <Button
                type='button'
                variant='outline'
                onClick={handleClose}
                disabled={isLoading}
                className='border-slate-600 text-slate-300 hover:bg-slate-700'
              >
                Cancel
              </Button>
              <Button type='submit' disabled={isLoading} className='bg-blue-600 hover:bg-blue-700 text-white'>
                {isLoading ? 'Creating...' : 'Create Membership'}
              </Button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};
